import React, { useCallback, useLayoutEffect, useRef } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { MenuList } from '@atlaskit/navigation-system';
import { GroupDropIndicator } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/group-drop-indicator';
import { Box } from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import GlobalShortcuts, {
	type GlobalShortcutsDragAndDropChangeEvent,
	type GlobalShortcutsItem,
} from '@atlassian/global-shortcuts';
import { useL1DragState } from '@atlassian/navigation-drag-and-drop';

import type { GlobalSideNavigationProps } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import { useInViewport } from '../../common/utils/use-in-viewport';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { useOnSave } from '../../common/utils/use-on-save';
import { useItemsAndShortcuts } from '../../controllers/items-and-shortcuts-provider';
import { useOptions } from '../../controllers/options-provider';
import { usePeekMenuId } from '../../controllers/peek-menu-id-providers';
import { useGlobalShortcutsTransformation } from '../../controllers/transformation/use-global-shortcuts-transformation';
import { useUserMenuDataShapes } from '../../controllers/user-state/user-menu-data-shapes-provider';
import { PEEK_TEST_ID, SHORTCUTS_TEST_ID } from '../constants';
import MoreMenu from '../more-menu';
import { INTERNAL_GLOBAL_ITEMS } from '../more-menu/constants';
import { HideFromSidebarButton } from '../more-menu/hide-from-sidebar-button';

export function PostMainItems({
	postItemsElem,
	postMainElem,
	draggingL1Item = false,
}: PostMainItemsProps) {
	const { appLabel, disableMenuCustomization, l1DragAndDropEnabled } = useOptions();

	const { allItems, growthItemShortcuts, shortcuts } = useItemsAndShortcuts();
	const peekMenuId = usePeekMenuId();

	const { hiddenItemMenuIds, hiddenShortcutMenuIds, visibleShortcutMenuIds, itemUserMenuData } =
		useUserMenuDataShapes();

	const { fireClickedSideNavItemEvent } = useNavAnalytics();

	const containerRef = useRef(null);
	const { hasBeenInViewport } = useInViewport({ ref: containerRef });

	const onSave = useOnSave();

	const {
		shortcuts: shortcutsWithoutMenuId,
		shortcutsWithMenuId,
		getUpdatedShortcutCustomizationData,
	} = useGlobalShortcutsTransformation({
		shortcutsConfig: shortcuts,
		visibleShortcuts: visibleShortcutMenuIds,
		growthShortcutsConfig: growthItemShortcuts,
	});

	const handleSaveShortcutsCustomization = useCallback(
		async (shortcutOrderPositions: number[]) => {
			const updatedShortcutCustomizationData = getUpdatedShortcutCustomizationData(
				shortcutsWithMenuId,
				shortcutOrderPositions,
			);

			await onSave(itemUserMenuData, updatedShortcutCustomizationData);
		},
		[onSave, itemUserMenuData, shortcutsWithMenuId, getUpdatedShortcutCustomizationData],
	);

	const { groupDropIndicatorRef } = useL1DragState({ l1DragAndDropEnabled });

	const handleClickMoreMenuItem = useCallback(() => {
		fireClickedSideNavItemEvent(INTERNAL_GLOBAL_ITEMS.more, 'postMain');
	}, [fireClickedSideNavItemEvent]);

	const handleClickCustomizeItem = useCallback(() => {
		fireClickedSideNavItemEvent(INTERNAL_GLOBAL_ITEMS.customize, 'postMain');
	}, [fireClickedSideNavItemEvent]);

	const peekItem = !!peekMenuId && hiddenItemMenuIds.includes(peekMenuId) && allItems[peekMenuId];

	const moreMenu = (
		<MoreMenu
			items={allItems}
			shortcutItems={shortcuts}
			itemIds={hiddenItemMenuIds}
			shortcutItemIds={hiddenShortcutMenuIds}
			appLabel={appLabel}
			onClickMoreMenuTrigger={handleClickMoreMenuItem}
			onClickCustomizeSidebarMenuItem={handleClickCustomizeItem}
		/>
	);

	return (
		<Stack space="space.100">
			{postItemsElem}
			{!!shortcutsWithoutMenuId.length && (
				<Box ref={containerRef}>
					<GlobalAppShortcuts
						shortcutItems={shortcutsWithoutMenuId}
						hasBeenInViewport={hasBeenInViewport}
						handleSaveShortcutCustomization={handleSaveShortcutsCustomization}
					/>
				</Box>
			)}
			{peekItem && (
				<MenuList testId={PEEK_TEST_ID}>
					<NavItem key={peekMenuId} id={peekMenuId} item={peekItem} isInMoreMenu peek />
				</MenuList>
			)}
			{!disableMenuCustomization &&
				(l1DragAndDropEnabled ? (
					<GroupDropIndicator ref={groupDropIndicatorRef} isActive={draggingL1Item}>
						<Box xcss={cx(draggingL1Item ? styles.hidden : styles.visible)}>{moreMenu}</Box>
						<Box xcss={cx(draggingL1Item ? styles.visible : styles.hidden)}>
							<HideFromSidebarButton />
						</Box>
					</GroupDropIndicator>
				) : (
					moreMenu
				))}
			{postMainElem}
		</Stack>
	);
}

export interface PostMainItemsProps
	extends Pick<GlobalSideNavigationProps, 'postItemsElem' | 'postMainElem'> {
	draggingL1Item?: boolean;
}

const styles = cssMap({
	visible: {
		display: 'block',
	},
	hidden: {
		display: 'none',
	},
});

function GlobalAppShortcuts({
	hasBeenInViewport,
	handleSaveShortcutCustomization,
	shortcutItems,
}: {
	handleSaveShortcutCustomization: GlobalShortcutsDragAndDropChangeEvent;
	hasBeenInViewport?: boolean;
	shortcutItems: GlobalShortcutsItem[];
}) {
	const { app, cloudId, userId, orgId, l1DragAndDropEnabled } = useOptions();

	const { fireViewedShortcutsEvent } = useNavAnalytics();

	useLayoutEffect(() => {
		if (hasBeenInViewport) {
			fireViewedShortcutsEvent(shortcutItems);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasBeenInViewport]);

	return (
		<GlobalShortcuts
			promoteSite
			orgId={orgId}
			cloudId={cloudId}
			userId={userId}
			product={app}
			forceStatic={true} // We don't need to call the BFF in GAS because we already did (see call to useGlobalShortcutsData() above).
			testId={SHORTCUTS_TEST_ID}
			shortcutItems={shortcutItems}
			// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
			enableDragAndDrop={l1DragAndDropEnabled}
			handleDragAndDropChange={handleSaveShortcutCustomization}
		/>
	);
}
